import * as React from "react";
import { mergeStyleSets } from "@fluentui/react";

export interface RecentViewResourceItem {
  /**
   * Name of recent view resource item
   */
  name: string;

  /**
   * URL of recent view resource item
   */
  url: string;

  /**
   * Type of recent view resource item
   */
  type: string;

  /**
   * LastViewed of recent view resource item
   */
  lastViewed: string;

  /**
   * Image of recent view resource item
   */
  image?: React.ReactNode;
}

export interface OverviewRecentViewResourceSection {
  /**
   * TestId of recent view resource section
   */
  testId?: string;

  /**
   * Title of recent view resource section
   */
  title?: string;

  /**
   * Recent view resource section table items
   */
  items: RecentViewResourceItem[];

  /**
   * Recent view resource section table items header
   */
  itemsHead?: string[];
}

const classNames = mergeStyleSets({
  overviewRecentViewResource: { paddingBottom: "40px" },
  overviewRecentViewResourceTitle: {
    fontWeight: 600,
    fontSize: "18px",
    marginBottom: "18px",
  },
  overviewRecentViewResourceTable: {
    width: "100%",
    textAlign: "left",
    fontSize: "12px",
    borderCollapse: "collapse",
    thead: {
      tr: { borderBottom: "1px solid #DCDADC" },
      th: { fontWeight: "normal" },
    },
    tbody: {
      tr: { borderBottom: "1px solid #DCDADC" },
      td: {
        a: {
          fontSize: "12px",
          color: "#0078D4",
          textDecoration: "none",
          ":hover": { textDecoration: "underline" },
        },
      },
    },
  },
  overviewRecentViewResourceTableItemIcon: { paddingRight: "4px" },
});

/**
 *
 * OverviewLinkSectionTemplate is a component to show the overview recent view resource section
 *
 */
export const OverviewRecentViewResourceSectionTemplate = ({
  title,
  items,
  testId,
  itemsHead,
}: OverviewRecentViewResourceSection): JSX.Element => (
  <div data-test-id={testId} className={classNames.overviewRecentViewResource}>
    {title && <div className={classNames.overviewRecentViewResourceTitle}>{title}</div>}
    <table className={classNames.overviewRecentViewResourceTable}>
      {itemsHead && (
        <thead>
          <tr key="header">
            {itemsHead.map(
              (itemHead, i) => (
                <th
                  scope="col"
                  key={`overview-link-section-header-${i.toString()}`}
                >
                  {itemHead}
                </th>
              ),
            )}
          </tr>
        </thead>
      )}
      {items && (
        <tbody>
          {items.map(
            (item, i) => (
              <tr
                key={`overview-link-section-${i.toString()}`}
              >
                <td
                  key={`overview-link-section-${i.toString()}-name`}
                >
                  <a rel="noreferrer" target="_blank" href={item.url}>
                    {item.image && (
                      <span
                        className={classNames.overviewRecentViewResourceTableItemIcon}
                      >
                        {item.image}
                      </span>
                    )}
                    {item.name}
                  </a>
                </td>
                <td
                  key={`overview-link-section-${i.toString()}-type`}
                >
                  {item.type}
                </td>
                <td
                  key={`overview-link-section-${i.toString()}-last-view`}
                >
                  {item.lastViewed}
                </td>
              </tr>
            ),
          )}
        </tbody>
      )}
    </table>
  </div>
);
